import { TypePolicies } from '@apollo/client';
import { gql } from 'graphql-tag';
import { defaultKeyArgsFunction } from './apolloCache/defaultKeyArgsFunction';
import { addParentConnection } from './apolloCache/addParentConnection';
import { readWithDefault } from './apolloCache/readWithDefault';
import { saveParentId } from './apolloCache/saveParentId';
import { mergeIncomingAndFillNulls } from './apolloCache/mergeIncomingAndFillNulls';
import {
  boardToCardsRelation,
  listToCardsRelation,
} from './apolloCache/relation';
import { Reference } from '@apollo/client/utilities';
import { queryMap } from './resolvers';
import {
  restResourceFieldPolicies,
  batchRestResourceFieldPolicies,
  readMemberMe,
} from './restResourceResolver/restResourceCacheRedirects';
import { OrganizationCardsArgs, Organization_Limits } from './generated';
import { idCache } from '@trello/shortlinks';

// Merging arrays of References with deduplication by __ref
const mergeRefArrays = (...arrs: Reference[][]): Reference[] => {
  return [
    ...arrs
      .flat()
      .filter((item) => item !== undefined)
      .reduce((map: Map<string, Reference>, item: Reference) => {
        const key = item.__ref;
        map.has(key) || map.set(key, item);
        return map;
      }, new Map<string, Reference>())
      .values(),
  ];
};

const fieldPolicies = {
  // eslint-disable-next-line @trello/no-module-logic
  ...restResourceFieldPolicies(queryMap),
  // eslint-disable-next-line @trello/no-module-logic
  ...batchRestResourceFieldPolicies(queryMap),
};

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      ...fieldPolicies,
      member: {
        read: readMemberMe,
      },
    },
  },
  Board: {
    fields: {
      cards: {
        // We have to use it if both read and merge function are defined on the field
        // Otherwise Apollo replaces it with `keyArgs: false`
        // https://github.com/apollographql/apollo-client/blob/2553695750f62657542792e22d0abe9b50a7dab2/src/cache/inmemory/policies.ts#L462
        keyArgs: defaultKeyArgsFunction,
        read: saveParentId,
        // eslint-disable-next-line @trello/no-module-logic
        merge: addParentConnection(boardToCardsRelation),
      },
      prefs: {
        merge: true,
      },
      templateGallery: {
        merge: true,
      },
      myPrefs: {
        merge: mergeIncomingAndFillNulls,
      },
    },
    merge: (existing, incoming, { mergeObjects, readField }) => {
      const id =
        (existing !== undefined && readField('id', existing)) || incoming?.id;
      const shortLink =
        (existing !== undefined && readField('shortLink', existing)) ||
        incoming?.shortLink;
      if (!idCache.getBoardId(shortLink) && shortLink) {
        idCache.setBoardId(shortLink, id);
      }

      return mergeObjects(existing, incoming);
    },
  },
  List: {
    fields: {
      cards: {
        // We have to use it if both read and merge function are defined on the field
        // Otherwise Apollo replaces it with `keyArgs: false`
        // https://github.com/apollographql/apollo-client/blob/2553695750f62657542792e22d0abe9b50a7dab2/src/cache/inmemory/policies.ts#L462
        keyArgs: defaultKeyArgsFunction,
        read: saveParentId,
        // eslint-disable-next-line @trello/no-module-logic
        merge: addParentConnection(listToCardsRelation),
      },
    },
  },
  Card: {
    fields: {
      badges: {
        merge: true,
      },
      cover: {
        merge: true,
      },
    },
    merge: (existing, incoming, { mergeObjects, readField, cache }) => {
      const id =
        (existing !== undefined && readField('id', existing)) ||
        incoming?.id ||
        readField('id', incoming);
      const shortLink =
        (existing !== undefined && readField('shortLink', existing)) ||
        incoming?.shortLink ||
        readField('shortLink', incoming);
      if (!idCache.getCardId(shortLink) && shortLink) {
        idCache.setCardId(shortLink, id);
      }

      /**
       * when we add a card via socket update or adding directly, the response
       * from server will not contain checklists. If we don't do the following,
       * we'd make network requests to get data we already have because of a cache
       * miss for checklists. To fix that, this writes the an empty checklists
       * array to the cache when a card is added and there were not previously
       * checklsits present on the card in the cache
       */
      if (
        existing &&
        !readField('checklists', existing) &&
        incoming &&
        !readField('checklists', incoming)
      ) {
        cache.writeFragment({
          id: cache.identify(existing || incoming),
          fragment: gql`
            fragment CardChecklists on Card {
              checklists(filter: all) {
                id
              }
              checklistNoArgs: checklists {
                id
              }
            }
          `,
          data: {
            checklists: [],
            checklistNoArgs: [],
          },
        });
      }

      return mergeObjects(existing, incoming);
    },
  },
  Checklist: {
    fields: {
      pos: {
        // -1 means position is unknown. See `calcPos` in app/scripts/lib/util/index.js
        read: readWithDefault(-1),
      },
    },
  },
  Enterprise: {
    fields: {
      paidAccount: {
        merge: true,
      },
      claimableOrganizations: {
        keyArgs: ['name'],
        read(existing, { args }) {
          return existing && existing?.cursor === args?.cursor
            ? undefined
            : existing;
        },
        merge(existing, incoming) {
          return {
            ...incoming,
            organizations: mergeRefArrays(
              existing?.organizations || [],
              incoming.organizations,
            ),
          };
        },
      },
    },
  },
  Organization: {
    fields: {
      paidAccount: {
        merge: true,
      },
      prefs: {
        merge: true,
      },
      cards: {
        keyArgs: (
          args: Partial<OrganizationCardsArgs> | null,
          { fieldName },
        ): string => {
          if (!args) {
            // https://github.com/apollographql/apollo-client/blob/d403a072b81fb9b10102d19ee636fa56186f9385/src/cache/inmemory/policies.ts#L267
            return fieldName;
          }
          const { limit, cursor, date, ...rest } = args;

          const keyObj = {
            ...rest,
            // `date` is not a keyArg since it's used for pagination on the
            // calendar. However, its absence or presence is a keyArg, because
            // it indicates whether we are paginating cards by `cursor` (table
            // view) or loading cards by date range (calendar view). This
            // computation is why keyArgs is a function instead of array for
            // this field.
            hasDateRange: Boolean(date),
          };

          return `${fieldName}:${JSON.stringify(keyObj)}`;
        },
        merge: (existing, incoming) => {
          return {
            ...incoming,
            cards: mergeRefArrays(existing?.cards || [], incoming?.cards || []),
          };
        },
      },
      /**
       * API for limits will return no count field until the org
       * hits the warnAt threshold, resulting in a cache miss for orgs.
       * Defaulting to null here fixing excessive requests for org
       */
      limits: {
        merge(existing: Organization_Limits, incoming: Organization_Limits) {
          return {
            ...existing,
            ...incoming,
            orgs: {
              ...(existing?.orgs || {}),
              ...(incoming?.orgs || {}),
              freeBoardsPerOrg: {
                ...(existing?.orgs?.freeBoardsPerOrg || {}),
                ...(incoming?.orgs?.freeBoardsPerOrg || {}),
                count:
                  incoming?.orgs?.freeBoardsPerOrg?.count ||
                  existing?.orgs?.freeBoardsPerOrg?.count ||
                  null,
              },
            },
          };
        },
      },
      enterprise: {
        merge: true,
      },
      ofEnterprise: {
        merge: true,
      },
    },
  },
  Member: {
    fields: {
      boardStars: {
        merge(existing = [], incoming: unknown[]) {
          return [...incoming];
        },
      },
      prefs: {
        merge: true,
      },
      paidAccount: {
        merge: true,
      },
      ofEnterprise: {
        merge: true,
      },
      nonPublic: {
        merge: mergeIncomingAndFillNulls,
      },
    },
  },
};
